import { useEffect, useState } from "react";
import api from "../lib/api";

const RESERVED = new Set(["www", "app", "api", "admin", "staging", "preview", "dev"]);
// Apex hostnames where a leading subdomain should be treated as a filmmaker slug.
// Add new custom domains here as they go live.
const FILMMAKER_APEX_HOSTS = [
  "greenscreen.pictures",
  "greenscreens.pictures",
  "seegreenscreen.com",
];

/**
 * Detect a filmmaker scope for the current request.
 * Priority:
 *  1. `?as=<slug>` query param (preview override, works inside any environment)
 *  2. Subdomain of the apex hostname (e.g. `mira-aleson.greenscreen.pictures`)
 *
 * Returns: { slug, filmmaker, isFilmmakerMode, loading }
 */
export function useFilmmaker() {
  const [state, setState] = useState({ slug: null, filmmaker: null, isFilmmakerMode: false, loading: true });

  useEffect(() => {
    const slug = detectFilmmakerSlug();
    if (!slug) {
      setState({ slug: null, filmmaker: null, isFilmmakerMode: false, loading: false });
      return;
    }
    let cancelled = false;
    api
      .get(`/filmmakers/${slug}`)
      .then(({ data }) => {
        if (cancelled) return;
        setState({ slug, filmmaker: data.filmmaker, isFilmmakerMode: true, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ slug: null, filmmaker: null, isFilmmakerMode: false, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

function detectFilmmakerSlug() {
  if (typeof window === "undefined") return null;
  // 1. Query param override
  const params = new URLSearchParams(window.location.search);
  const queryAs = params.get("as");
  if (queryAs) {
    // persist for the session so navigation stays in filmmaker mode
    try {
      window.sessionStorage.setItem("gs_filmmaker_as", queryAs);
    } catch (err) {
      // sessionStorage may be unavailable (Safari private mode, iframe sandbox)
      console.debug("sessionStorage set failed:", err?.message);
    }
    return queryAs;
  }
  try {
    const stored = window.sessionStorage.getItem("gs_filmmaker_as");
    if (stored) return stored;
  } catch (err) {
    console.debug("sessionStorage get failed:", err?.message);
  }
  // 2. Subdomain detection — only on known apex hosts
  const host = window.location.hostname;
  // ignore loopback / direct IPs
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  const apex = FILMMAKER_APEX_HOSTS.find((a) => host === a || host.endsWith(`.${a}`));
  if (!apex) return null;
  if (host === apex) return null; // apex itself, no subdomain
  const sub = host.slice(0, host.length - apex.length - 1).split(".")[0];
  if (!sub || RESERVED.has(sub)) return null;
  return sub;
}

export function clearFilmmakerScope() {
  try {
    window.sessionStorage.removeItem("gs_filmmaker_as");
  } catch (err) {
    console.debug("sessionStorage remove failed:", err?.message);
  }
}
